import { Box, Stack, Text, UseTreeReturnType } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { useHover } from "@mantine/hooks"
import React from "react"
import { RuleGroupType } from "react-querybuilder"

import { DigestResponses, DigestType } from "@costory/types/endpoints/digest"
import { DatePreset } from "@costory/types/prisma-client"

import { calculateRelativeChange } from "@costory/shared/utils/format"

import { CustomMarkdown } from "@costory/front/components/MarkdownEditor"
import { NodeRendering } from "@costory/front/pages/DigestV2/NodeRendering"
import { BillyAvailable } from "@costory/front/pages/DigestV2/components/BillyAvailable"
import { ChevronSVG } from "@costory/front/pages/DigestV2/components/Chevron"
import { NodeTree } from "@costory/front/pages/DigestV2/helper"

export type DigestNodeRowProps = {
  node: NodeTree
  level: number
  isExpanded: boolean
  hasActualChildren: boolean
  isDiffMode: boolean
  exploreInDrawer: (node: NodeTree) => void
  digest: DigestResponses.Investigation
  setManuallyCollapsedRows: React.Dispatch<React.SetStateAction<string[]>>
  nodesMap: Map<string, NodeTree>
  form: UseFormReturnType<DigestType>
  checkedNodes: Map<string, boolean>
  setCheckedNodes: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
  tree: UseTreeReturnType
  costColumn: string
  exclusionWhereClause: RuleGroupType
  minDiffToHide: number
  minRelativeDiffToHide: number
}

export const DigestNodeRow = ({
  node,
  level,
  isExpanded,
  hasActualChildren,
  isDiffMode,
  exploreInDrawer,
  digest,
  setManuallyCollapsedRows,
  form,
  checkedNodes,
  setCheckedNodes,
  tree,
  costColumn,
  exclusionWhereClause,
  minDiffToHide,
  minRelativeDiffToHide,
}: DigestNodeRowProps) => {
  const { hovered, ref } = useHover()
  const cannotEditContext =
    node.isOthers && (node.nodeProps.hiddenItems ?? []).length === 0
  const {
    relativeChange,
    formattedCost,
    formattedPreviousCost,
    formattedDiff,
    color,
  } = calculateRelativeChange(
    node.data.costCurrent - node.data.costPrevious,
    node.data.costPrevious,
    node.data.costCurrent,
  )

  const summary = node.nodeProps.summary
  const content = node.nodeProps.note || ""
  const allBadges = content
    .split("\n")
    .filter((line) => line.trim().startsWith("####"))

  // Limit badges to one row in collapsed state to prevent overflow
  const badgeMarkdown = isExpanded
    ? allBadges.join("\n")
    : allBadges.slice(0, 3).join("\n") // Show max 3 badges in collapsed state

  // Add indicator if there are more badges hidden
  const hasMoreBadges = !isExpanded && allBadges.length > 3

  // Determine if this node should show collapse/expand functionality
  const hasCollapsibleContent =
    hasActualChildren || node.nodeProps.imgUrl || content !== ""

  return (
    <React.Fragment key={node.value}>
      <Box w="100%" ref={ref}>
        <Box
          mb={8}
          ml={`${level * 24}px`}
          display="flex"
          pos="relative"
          fz="var(--mantine-font-size-sm)"
          bg={hovered ? "surfaceHover" : "surfaceCard"}
          style={{
            borderRadius: 7,
            transition: "box-shadow 0.2s, background-color 0.2s",
            boxShadow: hovered ? "var(--mantine-shadow-md)" : "",
            minHeight: "60px", // Fixed minimum height to prevent overlap
          }}
        >
          <Box
            py={17}
            display="flex"
            pos="relative"
            style={{
              flex: "0 0 40%",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                background: color,
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
              }}
            />
            <Box
              pl={15}
              display="flex"
              style={{
                alignItems: "center",
                transition: "padding-left 0.2s",
                width: "100%",
              }}
            >
              {hasCollapsibleContent && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    marginRight: 8,
                    cursor: "pointer",
                    transition: "transform 0.2s",
                    transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setManuallyCollapsedRows((prev) =>
                      prev.includes(node.value)
                        ? prev.filter((val) => val !== node.value)
                        : [...prev, node.value],
                    )
                  }}
                  role="button"
                  aria-label={isExpanded ? "Collapse node" : "Expand node"}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      e.stopPropagation()
                      setManuallyCollapsedRows((prev) =>
                        prev.includes(node.value)
                          ? prev.filter((val) => val !== node.value)
                          : [...prev, node.value],
                      )
                    }
                  }}
                  data-intercom-target="costory-tour-digest-node-expand-chevron"
                >
                  <ChevronSVG />
                </span>
              )}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <NodeRendering
                  node={node}
                  isDiffMode={isDiffMode}
                  hasChildren={hasActualChildren}
                  expanded={isExpanded}
                  exploreInDrawer={() => exploreInDrawer(node)}
                  level={level}
                  color={color}
                  form={form}
                  checkedNodes={checkedNodes}
                  setCheckedNodes={setCheckedNodes}
                  tree={tree}
                  setManuallyCollapsedRows={setManuallyCollapsedRows}
                  costColumn={costColumn}
                  digest={digest}
                  minDiffToHide={minDiffToHide}
                  minRelativeDiffToHide={minRelativeDiffToHide}
                />
              </div>
            </Box>
          </Box>
          <Box
            py={8}
            display="flex"
            style={{
              flex: "0 0 44%",
              width: "44%",
              flexDirection: "column",
              alignItems: "flex-start",
              minHeight: "44px", // Minimum height for content area
            }}
            pr={16}
          >
            <Text size="sm" fw={600} mb={4}>
              {summary}
            </Text>
            {/* Show only badges when collapsed, full content when expanded */}
            <Stack gap={4} align="flex-start" w="100%">
              <Box w="100%" style={{ margin: 0 }}>
                <CustomMarkdown
                  content={isExpanded ? content : badgeMarkdown}
                  maxWidth="100%"
                  imgWidth="85%"
                />
              </Box>
              {hasMoreBadges && (
                <Text size="xs" c="dimmed">
                  +{allBadges.length - 3} more badges
                </Text>
              )}
            </Stack>
          </Box>

          <Box
            py={8}
            pr={10}
            display="flex"
            style={{
              flex: "0 0 8%",
              width: "8%",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "flex-start",
              textAlign: "right",
            }}
          >
            {hovered && (
              <BillyAvailable
                position={node.nodeProps.position}
                datePreset={DatePreset.LAST_MONTH}
                period={form.values.period}
                exclusionWhereClause={exclusionWhereClause}
                costColumn={costColumn}
                cannotEditContext={cannotEditContext}
                onClick={() => exploreInDrawer(node)}
              />
            )}
            <CostCell
              cost={formattedCost}
              previousCost={formattedPreviousCost}
              isDiffMode={isDiffMode}
            />
          </Box>
          <Box
            py={8}
            pr={10}
            display="flex"
            style={{
              flex: "0 0 8%",
              alignItems: "flex-end",
              justifyContent: "flex-start",
              flexDirection: "column",
              textAlign: "right",
            }}
          >
            {isDiffMode && (
              <ChangeCell
                diff={formattedDiff}
                relativeChange={relativeChange}
                color={color}
              />
            )}
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  )
}

function CostCell({
  cost,
  previousCost,
  isDiffMode,
}: {
  cost: string
  previousCost: string
  isDiffMode: boolean
}) {
  return (
    <>
      <Stack justify="center" gap="0">
        <Text fz="sm" fw={500}>
          {cost}
        </Text>
        {isDiffMode && (
          <Text c="#888" fz="0.9em">
            {previousCost}
          </Text>
        )}
      </Stack>
    </>
  )
}

function ChangeCell({
  diff,
  relativeChange,
  color,
}: {
  diff: string
  relativeChange: string
  color: string
}): React.ReactNode {
  return (
    <Stack justify="center" gap="0">
      <Text fw={700} fz="sm" c={color}>
        {diff}
      </Text>
      <Text c={color} fz="0.9em">
        {relativeChange}
      </Text>
    </Stack>
  )
}
